import { useState, useEffect } from 'react'
import { Search, Image, Check, AlertCircle, Loader2, ExternalLink, Zap, ChevronRight } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import {
  useBrandsWithoutLogos,
  useLogoSearch,
  useLogoSave,
  useBatchLogoProcess,
  LogoSearchResult,
} from '@/lib/data/hooks/useLogoSearch'
import type { Brand } from '@/lib/data'

interface LogoSearchViewProps {
  client: SupabaseClient | null
}

export function LogoSearchView({ client }: LogoSearchViewProps) {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [manualSearch, setManualSearch] = useState('')
  const [selectedLogo, setSelectedLogo] = useState<string | null>(null)

  const { brands, isLoading: brandsLoading, refetch: refetchBrands } = useBrandsWithoutLogos(client)
  const { results, isSearching, error: searchError, usage, searchLogo, fetchUsage, clearResults } = useLogoSearch()
  const { saveLogo, isSaving } = useLogoSave(client)
  const { startBatchProcess, isProcessing, progress } = useBatchLogoProcess()

  useEffect(() => {
    fetchUsage()
  }, [fetchUsage])

  const handleBrandSelect = async (brand: Brand) => {
    setSelectedBrand(brand)
    setSelectedLogo(null)
    clearResults()
    await searchLogo(brand.brand_name)
  }

  const handleManualSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!manualSearch.trim()) return
    setSelectedBrand(null)
    setSelectedLogo(null)
    await searchLogo(manualSearch)
  }

  const handleSaveLogo = async () => {
    if (!selectedBrand || !selectedLogo) return
    const success = await saveLogo(selectedBrand.id, selectedLogo)
    if (success) {
      setSelectedBrand(null)
      setSelectedLogo(null)
      clearResults()
      refetchBrands()
    }
  }

  const handleBatchProcess = async () => {
    const brandIds = brands.slice(0, 10).map((b) => b.id)
    await startBatchProcess(brandIds)
    refetchBrands()
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Search size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to use logo search</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-main)]">Logo Search</h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Find and assign logos to brands using Google Image Search
          </p>
        </div>
        {usage && (
          <div className="text-right">
            <p className="text-sm text-[var(--text-muted)]">
              API Usage: {usage.searchesToday}/{usage.maxSearches}
            </p>
            <div className="w-32 h-2 bg-[var(--bg-element)] rounded-full mt-1 overflow-hidden">
              <div
                className="h-full bg-[var(--primary)] rounded-full transition-all"
                style={{ width: `${(usage.searchesToday / usage.maxSearches) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Manual Search */}
      <form onSubmit={handleManualSearch} className="max-w-xl">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={20} />
          <input
            type="text"
            value={manualSearch}
            onChange={(e) => setManualSearch(e.target.value)}
            placeholder="Search for any brand logo..."
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:border-[var(--primary)]"
          />
          <button
            type="submit"
            disabled={isSearching || !manualSearch.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            Search
          </button>
        </div>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Brands Without Logos */}
        <div className="lg:col-span-1">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
              <h3 className="font-semibold text-[var(--text-main)]">Brands Without Logos</h3>
              <span className="text-sm text-[var(--text-muted)]">{brands.length} remaining</span>
            </div>

            {/* Batch Process Button */}
            {brands.length > 0 && (
              <div className="p-3 border-b border-[var(--border)]">
                <button
                  onClick={handleBatchProcess}
                  disabled={isProcessing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 disabled:opacity-50"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Processing {progress.current}/{progress.total}...
                    </>
                  ) : (
                    <>
                      <Zap size={16} />
                      Auto-Process Next 10
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Brand List */}
            {brandsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--primary)]" />
              </div>
            ) : brands.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                <Image size={32} className="mx-auto mb-2 opacity-30" />
                <p>All brands have logos!</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto divide-y divide-[var(--border)]">
                {brands.map((brand) => (
                  <button
                    key={brand.id}
                    onClick={() => handleBrandSelect(brand)}
                    className={`w-full p-3 text-left hover:bg-[var(--bg-element)] transition-colors flex items-center justify-between ${
                      selectedBrand?.id === brand.id ? 'bg-[var(--primary)]/10' : ''
                    }`}
                  >
                    <div>
                      <p className="text-[var(--text-main)] font-medium">{brand.brand_name}</p>
                      {brand.parent_company_name && (
                        <p className="text-[var(--text-muted)] text-xs">{brand.parent_company_name}</p>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-[var(--text-muted)]" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Search Results */}
        <div className="lg:col-span-2">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
              <h3 className="font-semibold text-[var(--text-main)]">
                {selectedBrand ? `Results for "${selectedBrand.brand_name}"` : 'Search Results'}
              </h3>
              {selectedBrand && selectedLogo && (
                <button
                  onClick={handleSaveLogo}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-1.5 bg-emerald-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {isSaving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                  Save Selected Logo
                </button>
              )}
            </div>

            {/* Results Grid */}
            <div className="p-4">
              {isSearching ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)] mb-4" />
                  <p className="text-[var(--text-muted)]">Searching for logos...</p>
                </div>
              ) : searchError ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertCircle size={32} className="text-red-400 mb-4" />
                  <p className="text-red-400">{searchError}</p>
                  <p className="text-[var(--text-muted)] text-sm mt-2">
                    Make sure the logo search server is running on port 3000
                  </p>
                </div>
              ) : results.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
                  <Search size={32} className="mb-4 opacity-30" />
                  <p>Select a brand or search to find logos</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {results.map((result, index) => (
                    <LogoResultCard
                      key={index}
                      result={result}
                      isSelected={selectedLogo === result.url}
                      onSelect={() => setSelectedLogo(result.url)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface LogoResultCardProps {
  result: LogoSearchResult
  isSelected: boolean
  onSelect: () => void
}

function LogoResultCard({ result, isSelected, onSelect }: LogoResultCardProps) {
  const confidenceColor = {
    high: 'text-emerald-400 bg-emerald-500/10',
    medium: 'text-amber-400 bg-amber-500/10',
    low: 'text-red-400 bg-red-500/10',
  }[result.confidenceLevel]

  return (
    <button
      onClick={onSelect}
      className={`relative group rounded-xl overflow-hidden border-2 transition-all ${
        isSelected
          ? 'border-[var(--primary)] ring-2 ring-[var(--primary)]/30'
          : 'border-[var(--border)] hover:border-[var(--text-muted)]'
      }`}
    >
      <div className="aspect-square bg-[var(--bg-element)] flex items-center justify-center p-2">
        <img
          src={result.thumbnailUrl || result.url}
          alt={result.title}
          className="max-w-full max-h-full object-contain"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23666" x="50" y="55" text-anchor="middle" font-size="12">No Image</text></svg>'
          }}
        />
      </div>

      {/* Confidence Badge */}
      <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${confidenceColor}`}>
        {result.confidence}%
      </div>

      {/* Selected Indicator */}
      {isSelected && (
        <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[var(--primary)] flex items-center justify-center">
          <Check size={14} className="text-white" />
        </div>
      )}

      {/* Domain */}
      <div className="p-2 bg-[var(--bg-card)]">
        <p className="text-xs text-[var(--text-muted)] truncate">{result.domain}</p>
      </div>

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <a
          href={result.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
        >
          <ExternalLink size={20} className="text-white" />
        </a>
      </div>
    </button>
  )
}
